import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from "../components/ui/button";
import { CheckCircle2, ArrowLeft } from "lucide-react";

export default function ServiceArea() {
  const { slug } = useParams();
  const { t } = useLanguage();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Determine translation key based on slug
  let areaKey = "";
  if (slug === 'west-island') areaKey = 'west_island';
  else if (slug === 'tmr') areaKey = 'tmr';
  else if (slug === 'plateau-rosemont') areaKey = 'plateau';
  else if (slug === 'south-shore-laval') areaKey = 'south_shore';

  if (!areaKey || !t.areas || !t.areas[`${areaKey}_title`]) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white pt-32 flex flex-col items-center">
        <h1 className="text-4xl font-bold mb-4">Area Not Found</h1>
        <Link to="/" className="text-red-500 hover:underline">Return Home</Link>
      </div>
    );
  }

  const title = t.areas[`${areaKey}_title`];
  const description = t.areas[`${areaKey}_desc`];

  // Placeholder images for each area - using high quality Unsplash images
  const images = {
    west_island: "https://images.unsplash.com/photo-1558905544-3e3563d77899?q=80&w=2070&auto=format&fit=crop",
    tmr: "https://images.unsplash.com/photo-1596481768453-8befafc2d7ae?q=80&w=2070&auto=format&fit=crop",
    plateau: "https://images.unsplash.com/photo-1623298317883-6b70254edf31?q=80&w=2070&auto=format&fit=crop",
    south_shore: "https://images.unsplash.com/photo-1764173040050-b7a87824bc22?q=80&w=2070&auto=format&fit=crop"
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white pt-20">
      {/* Hero Section */}
      <div className="relative h-[400px] w-full overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images[areaKey]})` }}
        >
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative container mx-auto px-4 h-full flex flex-col justify-center">
          <Link to="/" className="inline-flex items-center text-neutral-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
          </Link>
          <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white">
            {title}
          </h1>
          <div className="flex items-center gap-2 text-red-500 font-semibold uppercase tracking-wider">
            Service Area
          </div>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 grid md:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold mb-4 text-white">Local Expertise</h2>
              <p className="text-xl text-neutral-400 leading-relaxed">
                {description}
              </p>
              <p className="text-neutral-400 mt-4 leading-relaxed">
                Paysagiste A Catalogna understands the unique soil conditions, bylaws, and architectural styles of {title}. 
                We tailor our services to enhance the specific character of your neighborhood.
              </p>
            </div>

            <div className="bg-neutral-900 p-8 rounded-xl border border-neutral-800">
              <h3 className="text-xl font-bold mb-6 text-white">Our Local Services</h3>
              <ul className="space-y-4">
                {[
                  "Residential Landscaping",
                  "Hardscaping & Patios",
                  "Asphalt & Paving",
                  "Property Maintenance"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <span className="text-neutral-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA Sidebar */}
          <div className="space-y-8">
            <div className="bg-black p-8 rounded-xl border border-neutral-800 sticky top-24">
              <h3 className="text-2xl font-bold mb-4 text-white">Get a Quote in {title}</h3>
              <p className="text-neutral-400 mb-6">
                Looking for a landscaper in {title}? Contact us today for a free consultation.
              </p>
              <Button 
                className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
                onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Request Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
